import { Theme } from '@material-ui/core';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { IResellerConfig } from '~/config/shared/reseller-config';
import { track } from '~/src/dash/analytics/analytics';
import { IUser } from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { getMediaSize, isWithinMediaSizeOrBelow, Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { getDemoSSAIUsername } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  IHistoryProps,
  IReactChildren,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  backgroundColor,
  container,
  drawerWidth,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount, isSamsungPublisher } from '~/src/dash/utils/ui';
import { BaseConfig, BaseContext, BaseContextState } from '~/src/dash/views/base/contexts';
import { bidderGroupsListViewConfig } from '~/src/dash/views/bidders/Bidders/tabs/BidderGroupsListView/config';
import { biddersListViewConfig } from '~/src/dash/views/bidders/Bidders/tabs/BiddersListView/config';
import { biddersNavigationConfig } from '~/src/dash/views/bidders/config';
import { channelGroupsConfig } from '~/src/dash/views/channels/ChannelGroups/config';
import { channelMappingsConfig } from '~/src/dash/views/channels/ChannelMappings/config';
import { channelsConfig } from '~/src/dash/views/channels/Channels/config';
import { channelsNavigationConfig } from '~/src/dash/views/channels/config';
import { creativeReviewListViewConfig } from '~/src/dash/views/brand-safety/creative-review/config';
import {
  dashboardLiveConfig,
  getDashboardLiveConfig,
} from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardLiveConfig';
import {
  dashboardOverviewConfig,
  getDashboardOverviewConfig,
} from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { dashboardParentOverviewConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardParentOverviewConfig';
import { dashboardPublicaAnalyticsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardPublicaAnalyticsConfig';
import { dashboardsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardsConfig';
import { navBarConfig } from '~/src/dash/views/navbar/configs/defaultCfg';
import { iasWhiteLabelConfig } from '~/src/dash/views/navbar/configs/iasCfg';
import { navbarConfigSSAI } from '~/src/dash/views/navbar/configs/ssaiCfg';
import { NavBar } from '~/src/dash/views/navbar/NavBar';
import { getNavWidth } from '~/src/dash/views/navbar/NavBar/common/consts';
import { platformConnectNavigationConfig } from '~/src/dash/views/platform-connect/config';
import { connectionsViewConfig } from '~/src/dash/views/platform-connect/Connections/config';
import { draftOfferNewConfig } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { pendingOffersViewConfig } from '~/src/dash/views/platform-connect/PendingOffers/config';
import { audienceManagerListViewConfig } from '~/src/dash/views/segments/audience/AudienceManagerSettingsListView/config';
import { audienceSegmentsNavigationConfig } from '~/src/dash/views/segments/audience/AudienceSegments/config';
import { liverampConfig } from '~/src/dash/views/segments/audience/liveramp/distribution/config';
import { inventoryParametersFormConfig } from '~/src/dash/views/tools/Forecasting/InventoryParameters/config';
import { targetingPresetListViewConfig } from '~/src/dash/views/tools/TargetingPresets/config';
import { targetingPresetFormConfig } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import { useTranslation } from 'react-i18next';
import { reportingListConfig } from '~/src/dash/views/reporting/ReportingListView/config';
import { orderLineItemsListViewConfig } from '~/src/dash/views/campaign/OrderLineItemsListView/config';
import { lineItemsListViewConfig } from '~/src/dash/views/campaigns/line-items/LineItemsListView/config';
import { ordersListViewConfig } from '~/src/dash/views/campaigns/orders/OrdersListView/config';
import { dealsListViewConfig } from '~/src/dash/views/dealV2/DealsList/config';
import { dashboardAuctionInsightsConfig } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/configs/mainCfg';
import { inventoryRulesNavigationConfig } from '~/src/dash/views/inventory-rules-v2/Rules/config';
import { inventoryRulesListConfig } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/config';

// update this if you need a custom config for a reseller
const getConfig = (reseller: string, user: IUser) => {
  if (getDemoSSAIUsername() === user.Username) {
    return navbarConfigSSAI;
  }
  switch (reseller) {
    case 'ias publisher':
      return iasWhiteLabelConfig;
    case 'publica':
    default:
      return navBarConfig;
  }
};

interface IProps
  extends IReactChildren,
    IWithStylesProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

export const PublisherBaseView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
    styles: (theme: Theme) => {
      return {
        appbar: {
          position: 'sticky',
          top: 0,
          marginLeft: 16,
          backgroundColor: primaryColor[0],
        },
        appbarTitle: {
          color: whiteColor,
          flexGrow: 1,
        },
        mainPanel: {
          position: 'relative',
          float: 'right',
          width: `calc(100vw - ${drawerWidth}px)`,
        },
        content: {
          padding: '1rem',
          minHeight: 'calc(100vh - 64px)',
          background: backgroundColor,
        },
        container,
        createSiteButton: {
          marginRight: theme.spacing(2),
        },
      };
    },
  },
  function (props: IProps) {
    const { classes, children, session, user, location, dispatch } = props;
    const isSuperUser = hasRoleSuperUser(user.user);
    const isSamsungPub = isSamsungPublisher(user.publisher.ID);
    const isDemoUser = isDemoAccount(user.user.Username);
    const isMounted = useRef(false);
    const [isNavExpanded, setIsNavExpanded] = useState(true);
    const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);
    const navWidth = getNavWidth(isNavExpanded, isScrollbarVisible);
    const theme = (window as any).CONFIG.theme;

    const ref = useRef(null);
    const [size, setSize] = useState([null, null]);
    const [width, height] = size;

    const { CurrencyType, ExchangeValueToUSD } = session.publisher.ExchangeRate;

    const RESELLER_CONFIG = (window as any).CONFIG as IResellerConfig;

    const { t } = useTranslation();

    useEffect(() => {
      if (isSuperUser) {
        getDashboardOverviewConfig(isSuperUser, isSamsungPub);
        getDashboardLiveConfig(isSuperUser, isSamsungPub);
      }
    }, []);

    const updateSize = () => {
      // @ts-ignore
      const currentWidth = ref.current ? ref.current.clientWidth : 0;
      // @ts-ignore
      const currentHeight = ref.current ? ref.current.clientHeight : 0;
      setSize([currentWidth, currentHeight]);
    };

    useLayoutEffect(() => {
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, []);

    // if initally loaded on a medium screen, collapse the sidebar
    useEffect(() => {
      if (!isMounted.current && size[0]) {
        if (isWithinMediaSizeOrBelow('medium', getMediaSize(size[0]))) {
          setIsNavExpanded(false);
        }
        isMounted.current = true;
      }
    }, [size]);

    const handleNavExpand = () => {
      track(`Sidebar - ${isNavExpanded ? 'Collapse' : 'Expand'} Nav`, { isSuperUser }, user.user);
      setIsNavExpanded(!isNavExpanded);
    };

    return (
      <BaseContext.Provider
        value={{
          children,
          classes,
          currencyMultiplier: ExchangeValueToUSD,
          currencySymbol: convertECurrencyTypeToSymbol(CurrencyType || 0),
          currencyType: CurrencyType,
          dispatch,
          isSuperUser,
          isDemoUser,
          location,
          resellerCfg: RESELLER_CONFIG,
          session,
          user,
          t,
        }}
      >
        <BaseContextState.Provider
          value={{
            isNavExpanded,
            totalWidth: width,
            totalHeight: height,
            mediaSize: getMediaSize(width),
          }}
        >
          <BaseConfig.Provider
            value={{
              AudienceSegments: {
                AudienceSegments: {
                  Main: audienceSegmentsNavigationConfig,
                },
              },
              Bidders: {
                Main: biddersNavigationConfig,
                All: biddersListViewConfig,
                Groups: bidderGroupsListViewConfig,
              },
              BrandSafety: {
                CreativeReview: {
                  Main: creativeReviewListViewConfig,
                },
              },
              Campaigns: {
                LineItems: {
                  Main: lineItemsListViewConfig,
                  OrderLineItems: orderLineItemsListViewConfig,
                },
                Orders: {
                  Main: ordersListViewConfig,
                },
              },
              Channels: {
                Main: channelsNavigationConfig,
                All: channelsConfig,
                Groups: channelGroupsConfig,
                Mappings: channelMappingsConfig,
              },
              Dashboards: {
                Main: dashboardsConfig,
                Overview: dashboardOverviewConfig,
                Live: dashboardLiveConfig,
                ParentOverview: dashboardParentOverviewConfig,
                PublicaAnalytics: dashboardPublicaAnalyticsConfig,
                AuctionInsights: dashboardAuctionInsightsConfig,
              },
              Deals: {
                Main: dealsListViewConfig,
              },
              InventoryRules: {
                Rules: {
                  Main: inventoryRulesNavigationConfig,
                  All: inventoryRulesListConfig,
                },
              },
              LiverampDistribution: {
                Main: liverampConfig,
              },
              PlatformConnect: {
                Main: platformConnectNavigationConfig,
                PendingOffers: pendingOffersViewConfig,
                Connections: connectionsViewConfig,
                DraftOfferNew: draftOfferNewConfig,
              },
              Reporting: {
                Main: reportingListConfig,
              },
              Segments: {
                Audience: {
                  Main: audienceManagerListViewConfig,
                },
              },
              Tools: {
                Forecasting: {
                  Main: {
                    InventoryParameters: inventoryParametersFormConfig,
                  },
                },
                Targeting: {
                  Main: targetingPresetListViewConfig,
                  Form: targetingPresetFormConfig,
                },
              },
            }}
          >
            <div
              ref={ref}
              style={{
                position: 'absolute',
                width: '100vw',
                height: '100vh',
                padding: 0,
                margin: 0,
              }}
            >
              <Panels autoSize style={{ backgroundColor }}>
                <Panel
                  scroll
                  enableSize
                  style={{
                    width: navWidth,
                    minWidth: navWidth,
                    transition: 'width 0.3s, min-width 0.3s',
                    backgroundColor: theme.menuBarBackground,
                  }}
                >
                  <NavBar
                    cfg={getConfig(RESELLER_CONFIG.title.toLowerCase(), user.user)}
                    handleNavExpand={handleNavExpand}
                    setIsScrollbarVisible={setIsScrollbarVisible}
                  />
                </Panel>
                <Panel maxHeight flex horizontalScroll>
                  {children}
                </Panel>
              </Panels>
            </div>
          </BaseConfig.Provider>
        </BaseContextState.Provider>
      </BaseContext.Provider>
    );
  },
);
